"use client";

import type React from "react";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  HStack,
  Icon,
  CloseButton,
  VStack,
  Box,
  Flex,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { callService } from "../../voice-services/callService";
import {
  agentService,
  type RetellLLM,
} from "../../voice-services/agentService";

interface MakeOutboundCallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  agentId: string | null;
}

/** Concatenate Retell LLM fields used on calls so {{vars}} in prompt / begin / tools / states JSON are discoverable */
function collectVoiceTemplateText(llm: RetellLLM | null): string {
  if (!llm) return "";
  const parts: string[] = [];
  if (llm.general_prompt) parts.push(llm.general_prompt);
  if (llm.begin_message) parts.push(llm.begin_message);
  if (Array.isArray(llm.general_tools) && llm.general_tools.length > 0) {
    try {
      parts.push(JSON.stringify(llm.general_tools));
    } catch {
      parts.push(String(llm.general_tools));
    }
  }
  if (Array.isArray(llm.states) && llm.states.length > 0) {
    try {
      parts.push(JSON.stringify(llm.states));
    } catch {
      parts.push(String(llm.states));
    }
  }
  return parts.join("\n");
}

export const MakeOutboundCallDialog: React.FC<MakeOutboundCallDialogProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  agentId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toNumber, setToNumber] = useState("");
  const [templateSourceText, setTemplateSourceText] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const toast = useToast();

  const modalContentBg = "white";
  const labelColor = "gray.700";
  const infoTextColor = "gray.600";

  useLayoutEffect(() => {
    if (isOpen && agentId) {
      setLoadingTemplate(true);
    }
  }, [isOpen, agentId]);

  useEffect(() => {
    if (!isOpen || !agentId) {
      setTemplateSourceText("");
      setDynamicVariables({});
      setLoadingTemplate(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const agent = await agentService.get(agentId);
        if (cancelled) return;

        if (
          agent.response_engine?.type === "retell-llm" &&
          agent.response_engine.llm_id
        ) {
          const llm = await agentService.getLLMData(
            agent.response_engine.llm_id
          );
          if (cancelled) return;
          setTemplateSourceText(collectVoiceTemplateText(llm));
        } else {
          setTemplateSourceText("");
        }
      } catch (e) {
        console.error(
          "Failed to load voice agent template for dynamic vars:",
          e
        );
        if (!cancelled) setTemplateSourceText("");
      } finally {
        if (!cancelled) setLoadingTemplate(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, agentId]);

  const extractedVariables = useMemo(() => {
    const regex = /{{\s*([^{}]+?)\s*}}/g;
    const variables = new Set<string>();
    let match: RegExpExecArray | null = regex.exec(templateSourceText);
    while (match) {
      const name = (match[1] || "").trim();
      if (name) variables.add(name);
      match = regex.exec(templateSourceText);
    }
    return Array.from(variables);
  }, [templateSourceText]);

  useEffect(() => {
    if (!isOpen) {
      setToNumber("");
      setDynamicVariables({});
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!toNumber) {
      toast({
        title: "Phone number is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!agentId) {
      toast({
        title: "No outbound agent configured",
        description:
          "Please configure an outbound agent for this phone number first",
        status: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await callService.createPhoneCall(agentId, phoneNumber, toNumber, {
        dynamic_vars_values: dynamicVariables,
      });

      toast({
        title: "Outbound call initiated",
        description: `Calling ${toNumber} from ${phoneNumber}`,
        status: "success",
        duration: 3000,
      });
      setToNumber("");
      setDynamicVariables({});
      onClose();
    } catch (error: any) {
      const data = error.response?.data;
      const detail = data?.detail;
      const detailStr =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join("; ")
            : detail != null
              ? String(detail)
              : undefined;
      toast({
        title: "Error making outbound call",
        description:
          data?.message ||
          detailStr ||
          "An error occurred while making the call",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="500px">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={4}
          color={labelColor}
        >
          Make Outbound Call
          <CloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <FormControl mb={4}>
            <FormLabel color={labelColor}>Phone Number</FormLabel>
            <Input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="e.g. +11234567890"
              borderColor={"gray.300"}
            />
          </FormControl>

          {agentId && loadingTemplate && (
            <Box
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={6}
              mb={4}
              minH="120px"
            >
              <Center flexDirection="column" gap={3}>
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="md"
                />
                <Text fontSize="sm" color={infoTextColor} textAlign="center">
                  Loading dynamic variables from your agent…
                </Text>
              </Center>
            </Box>
          )}

          {!loadingTemplate && extractedVariables.length > 0 && (
            <Box
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={4}
            >
              <Text fontWeight="semibold" fontSize="sm" color={labelColor}>
                Dynamic Variables
              </Text>
              <Text fontSize="xs" color={infoTextColor} mt={1} mb={3}>
                From your agent general prompt, begin message, tools, and flow
                states (Retell LLM)
              </Text>

              <Flex mb={2} fontWeight="medium" fontSize="xs" color={labelColor}>
                <Box flex="1">Variable Name</Box>
                <Box flex="1">Test Value</Box>
              </Flex>

              <VStack spacing={2} align="stretch">
                {extractedVariables.map((variable) => (
                  <HStack key={variable} spacing={2}>
                    <Input
                      size="sm"
                      value={variable}
                      isReadOnly
                      fontSize="xs"
                      bg="white"
                    />
                    <Input
                      size="sm"
                      placeholder="Enter value"
                      value={dynamicVariables[variable] || ""}
                      fontSize="xs"
                      onChange={(e) =>
                        setDynamicVariables((prev) => ({
                          ...prev,
                          [variable]: e.target.value,
                        }))
                      }
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <VStack
            spacing={2}
            color={infoTextColor}
            fontSize="sm"
            align={"start"}
          >
            <HStack spacing={2} color={infoTextColor}>
              <Icon as={InfoIcon} />
              <Text>International calls are currently not supported.</Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            colorScheme={"white"}
            textColor={"gray.800"}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!agentId}
          >
            Call
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
